import React, { useState } from 'react';
import { X, Camera, Video } from 'lucide-react';
import { useCreateSponsorshipUpdateMutation } from '../../store/slices/apiSlice';
import toast from 'react-hot-toast';

interface AddUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  childId: number;
  childName: string;
}

const AddUpdateModal: React.FC<AddUpdateModalProps> = ({
  isOpen,
  onClose,
  childId,
  childName
}) => {
  const [createUpdate, { isLoading }] = useCreateSponsorshipUpdateMutation();

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    content: '',
    update_type: 'progress_report' as 'progress_report' | 'photo_update' | 'video_update' | 'general_update',
    update_date: new Date().toISOString().split('T')[0]
  });

  const [files, setFiles] = useState<{
    images: File[];
    videos: File[];
  }>({
    images: [],
    videos: []
  });

  // File size limits (in bytes) - Reasonable limits for file uploads
  const FILE_LIMITS = {
    image: 5 * 1024 * 1024, // 5MB - Reasonable limit for images
    video: 50 * 1024 * 1024, // 50MB - Reasonable limit for videos
    document: 10 * 1024 * 1024, // 10MB - Reasonable limit for documents
    total: 100 * 1024 * 1024 // 100MB total - Reasonable limit for all files
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.6): Promise<File> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }

        canvas.width = width;
        canvas.height = height;

        // Draw and compress
        ctx?.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            } else {
              resolve(file);
            }
          },
          'image/jpeg',
          quality
        );
      };

      img.onerror = () => resolve(file);
      img.src = URL.createObjectURL(file);
    });
  };

  const validateFileSize = (file: File, type: 'image' | 'video' | 'document') => {
    const limit = FILE_LIMITS[type];
    if (file.size > limit) {
      toast.error(`${file.name} is too large. Maximum size allowed: ${formatFileSize(limit)}`);
      return false;
    }
    return true;
  };

  const calculateTotalSize = (allFiles: { images: File[]; videos: File[] }) => {
    return allFiles.images.reduce((sum, file) => sum + file.size, 0) +
      allFiles.videos.reduce((sum, file) => sum + file.size, 0);
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>, fileType: 'images' | 'videos') => {
    const selectedFiles = Array.from(e.target.files || []);

    // Process files (compress images if needed)
    const processedFiles: File[] = [];
    const fileTypeMap = {
      images: 'image' as const,
      videos: 'video' as const
    };

    for (const file of selectedFiles) {
      let processedFile = file;

      // Compress images if they're too large or close to limit
      if (fileType === 'images' && (file.size > FILE_LIMITS.image || file.size > FILE_LIMITS.image * 0.7)) {
        try {
          toast.loading(`Compressing ${file.name}...`);
          processedFile = await compressImage(file);
          toast.success(`${file.name} compressed from ${formatFileSize(file.size)} to ${formatFileSize(processedFile.size)}`);
        } catch (error) {
          console.error('Image compression failed:', error);
          toast.error(`Failed to compress ${file.name}. Using original file.`);
        }
      }

      if (validateFileSize(processedFile, fileTypeMap[fileType])) {
        processedFiles.push(processedFile);
      }
    }

    if (processedFiles.length === 0) return;

    // Check total size limit
    const newFiles = {
      ...files,
      [fileType]: [...files[fileType], ...processedFiles]
    };

    const totalSize = calculateTotalSize(newFiles);
    if (totalSize > FILE_LIMITS.total) {
      toast.error(`Total file size exceeds limit. Maximum allowed: ${formatFileSize(FILE_LIMITS.total)}`);
      return;
    }

    setFiles(newFiles);

    // Clear the input
    e.target.value = '';
  };

  const removeFile = (fileType: 'images' | 'videos', index: number) => {
    setFiles(prev => ({
      ...prev,
      [fileType]: prev[fileType].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    // Check total file size before submitting
    const totalSize = calculateTotalSize(files);
    if (totalSize > FILE_LIMITS.total) {
      toast.error(`Total file size (${formatFileSize(totalSize)}) exceeds limit (${formatFileSize(FILE_LIMITS.total)})`);
      return;
    }

    try {
      const updateData = {
        child_id: childId,
        title: formData.title,
        description: formData.description,
        content: formData.content,
        update_type: formData.update_type,
        update_date: formData.update_date,
        images: files.images,
        videos: files.videos
      };

      console.log('Submitting update with data:', {
        ...updateData,
        images: files.images.map(f => ({ name: f.name, size: f.size, type: f.type })),
        videos: files.videos.map(f => ({ name: f.name, size: f.size, type: f.type }))
      });

      const response = await createUpdate(updateData).unwrap();

      // Check if the response indicates success
      if (response.success) {
        toast.success('Update created successfully!');
        onClose();
      } else {
        // Handle case where API returns success: false
        toast.error(response.message || 'Failed to create update');
      }

      // Reset form
      setFormData({
        title: '',
        description: '',
        content: '',
        update_type: 'progress_report',
        update_date: new Date().toISOString().split('T')[0]
      });
      setFiles({
        images: [],
        videos: []
      });
    } catch (error: any) {
      console.error('Create update error:', error);
      console.error('Error details:', {
        status: error?.status,
        data: error?.data,
        message: error?.message,
        originalStatus: error?.originalStatus
      });

      // Handle different error scenarios
      if (error?.data) {
        // RTK Query error with data
        if (error.data.success === false) {
          toast.error(error.data.message || 'Failed to create update');
        } else {
          toast.error(error.data.message || 'Failed to create update');
        }
      } else if (error?.status === 413 || error?.data?.status === 413 || error?.originalStatus === 413) {
        toast.error('File size too large for server. Please reduce file sizes and try again.');
      } else if (error?.status === 400 || error?.originalStatus === 400) {
        const errorMessage = error?.data?.message || error?.data?.error || 'Invalid data provided. Please check your inputs.';
        toast.error(errorMessage);
      } else if (error?.status === 500 || error?.originalStatus === 500) {
        toast.error('Server error. Please try again later.');
      } else if (error?.error === 'TypeError: Failed to fetch' || error?.message?.includes('Failed to fetch')) {
        toast.error('Network error. Please check your connection and try again.');
      } else {
        const errorMessage = error?.data?.message || error?.data?.error || error?.message || 'Failed to create update. Please try again.';
        toast.error(errorMessage);
      }
    }
  };

  const handleClose = () => {
    onClose();
    // Reset form when closing
    setFormData({
      title: '',
      description: '',
      content: '',
      update_type: 'progress_report',
      update_date: new Date().toISOString().split('T')[0]
    });
    setFiles({
      images: [],
      videos: []
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Add Update for {childName}</h2>
            <p className="text-sm text-gray-600">Create a new sponsorship update</p>
          </div>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter update title"
              required
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description *
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Brief description of the update..."
              required
            />
          </div>

          {/* Content */}
          <div>
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-2">
              Content
            </label>
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Detailed content for the update..."
            />
          </div>

          {/* Type and Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label htmlFor="update_type" className="block text-sm font-medium text-gray-700 mb-2">
                Update Type
              </label>
              <select
                id="update_type"
                name="update_type"
                value={formData.update_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="progress_report">Progress Report</option>
                <option value="photo_update">Photo Update</option>
                <option value="video_update">Video Update</option>
                <option value="general_update">General Update</option>
              </select>
            </div>
            <div>
              <label htmlFor="update_date" className="block text-sm font-medium text-gray-700 mb-2">
                Update Date
              </label>
              <input
                type="date"
                id="update_date"
                name="update_date"
                value={formData.update_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>


          {/* File Uploads */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">Attachments</h3>
              <div className={`text-sm ${calculateTotalSize(files) > FILE_LIMITS.total * 0.8 ? 'text-orange-600' : 'text-gray-500'}`}>
                Total: {formatFileSize(calculateTotalSize(files))} / {formatFileSize(FILE_LIMITS.total)}
                {calculateTotalSize(files) > FILE_LIMITS.total * 0.8 && (
                  <span className="ml-2 text-xs">⚠️ Near limit</span>
                )}
              </div>
            </div>

            {/* File size limits info */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">File Size Limits</h4>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• Images: Max {formatFileSize(FILE_LIMITS.image)} each</div>
                <div>• Videos: Max {formatFileSize(FILE_LIMITS.video)} each</div>
                <div>• Total: Max {formatFileSize(FILE_LIMITS.total)} for all files</div>
              </div>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Images
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFileChange(e, 'images')}
                  className="hidden"
                  id="images"
                />
                <label
                  htmlFor="images"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Camera className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to upload images</span>
                </label>
              </div>
              {files.images.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.images.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <div className="flex-1">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile('images', index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Videos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Videos
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  onChange={(e) => handleFileChange(e, 'videos')}
                  className="hidden"
                  id="videos"
                />
                <label
                  htmlFor="videos"
                  className="flex flex-col items-center justify-center cursor-pointer"
                >
                  <Video className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Click to upload videos</span>
                </label>
              </div>
              {files.videos.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.videos.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <div className="flex-1">
                        <span className="text-sm text-gray-700">{file.name}</span>
                        <span className="text-xs text-gray-500 ml-2">({formatFileSize(file.size)})</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile('videos', index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating...' : 'Create Update'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddUpdateModal;
// this comment isadded for push code on github
